<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>
            Driver Detail
            <span class="fs-4" v-if="driverData && driverData.driverStatus && !isNewDriver"
                  v-html="driverStatus(driverData.driverStatus)"></span>
          </h2>
        </div>
      </div>
    </div>

    <div v-if="driverData && (driverData.driverStatus !== 'OFFLINE') && !isNewDriver" class="row">
      <!-- Map   -->
      <div class="col-12">
        <div class="card">
          <div id="map" style=" height: 45vh; width: 100%;"></div>
        </div>
      </div>
    </div>

    <!-- Onboarding Status Card — shown for all statuses except COMPLETE -->
    <div class="col-12" v-if="!isNewDriver && driverData && driverData.onboardingStatus !== 'COMPLETE'">
      <div class="card" :class="{
        'b-secondary bg-light': driverData.onboardingStatus === 'NOT_STARTED',
        'b-warning bg-light-warning': driverData.onboardingStatus === 'IN_PROGRESS' || driverData.onboardingStatus === 'PENDING_VERIFICATION',
        'b-danger bg-light-danger': driverData.onboardingStatus === 'REJECTED'
      }">
        <div class="card-header" :class="{
          'bg-secondary': driverData.onboardingStatus === 'NOT_STARTED',
          'bg-warning': driverData.onboardingStatus === 'IN_PROGRESS' || driverData.onboardingStatus === 'PENDING_VERIFICATION',
          'bg-danger': driverData.onboardingStatus === 'REJECTED'
        }">
          <h5>
            <i class="fa-solid fa-clipboard-check"></i>
            Onboarding Status
          </h5>
        </div>
        <div class="card-body">
          <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
            <div>
              <h4>
                Current Status:
                <span class="badge" :class="{
                  'bg-secondary': driverData.onboardingStatus === 'NOT_STARTED',
                  'bg-warning': driverData.onboardingStatus === 'IN_PROGRESS' || driverData.onboardingStatus === 'PENDING_VERIFICATION',
                  'bg-danger': driverData.onboardingStatus === 'REJECTED'
                }">
                  {{ driverData.onboardingStatus.replace(/_/g, ' ') }}
                </span>
              </h4>
              <p class="text-muted mb-0 mt-2" v-if="driverData.onboardingStatus === 'NOT_STARTED'">
                This driver has not yet submitted their application.
              </p>
              <p class="text-muted mb-0 mt-2" v-else-if="driverData.onboardingStatus === 'IN_PROGRESS'">
                This driver has submitted their application. Their background check is underway.
              </p>
              <p class="text-muted mb-0 mt-2" v-else-if="driverData.onboardingStatus === 'PENDING_VERIFICATION'">
                The background check has passed. This driver is awaiting admin document review.
              </p>
              <p class="text-muted mb-0 mt-2" v-else-if="driverData.onboardingStatus === 'REJECTED'">
                This driver's application has been rejected. They need to resubmit their information.
              </p>

              <!-- Onboarding checklist -->
              <div class="mt-3" v-if="driverData.independentDriver">
                <p class="fw-semibold mb-2">Driver Onboarding Checklist</p>
                <ul class="list-group list-group-flush border rounded">
                  <li class="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                    <span class="small"><i class="fa fa-id-badge me-2 text-muted"></i>Profile Photo</span>
                    <span class="badge rounded-pill" :class="driverData.hasProfilePicture ? 'bg-success' : 'bg-warning'">
                      {{ driverData.hasProfilePicture ? 'Uploaded' : 'Required' }}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                    <span class="small"><i class="fa fa-camera me-2 text-muted"></i>Vehicle Photos</span>
                    <span class="badge rounded-pill" :class="driverData.vehiclePhotosUploaded ? 'bg-success' : 'bg-warning'">
                      {{ driverData.vehiclePhotosUploaded ? 'Uploaded' : 'Required' }}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                    <span class="small"><i class="fa fa-credit-card me-2 text-muted"></i>Payment Setup (Stripe)</span>
                    <span class="badge rounded-pill" :class="driverData.stripeOnboarded ? 'bg-success' : 'bg-warning'">
                      {{ driverData.stripeOnboarded ? 'Complete' : 'Required' }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Admin actions -->
            <div class="d-flex flex-column gap-2" v-if="driverData.independentDriver">
              <button
                  class="btn btn-outline-primary btn-sm"
                  @click="showResendStatusEmailConfirmation"
                  :disabled="isProcessing">
                <i class="fa-solid fa-paper-plane me-1"></i>
                Resend Status Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Background Check Panel. Reloads the driver on resolve: an outcome re-derives onboarding status,
         and a stale badge above would read as the decision having failed. -->
    <div class="col-12" v-if="!isNewDriver && driverData && driverData.independentDriver">
      <DriverBackgroundCheckPanel :driverId="props.driverId" @resolved="loadDriverData" />
    </div>

    <!-- GST/HST Tax Registration Panel. Reloads the driver on resolve: recording a number re-derives
         onboarding status (often PENDING_VERIFICATION → COMPLETE), and a stale badge above would read
         as the resolve having failed. -->
    <div class="col-12" v-if="!isNewDriver && driverData && driverData.independentDriver">
      <DriverTaxRegistrationPanel :driverId="props.driverId" @resolved="loadDriverData" />
    </div>

    <div class="col-12">
      <div class="card">
        <div class="card-header d-flex justify-content-between gap-2">
          <h3>
            <span v-if="isNewDriver">New </span>
            <span v-else-if="editDriverInfo">Edit </span>
            Driver Information
          </h3>

          <div v-if="!editDriverInfo" class="d-flex gap-2">
            <button
                v-if="driverData && (driverData.onboardingStatus === 'COMPLETE' || driverData.onboardingStatus === 'PENDING_VERIFICATION')"
                class="btn btn-primary" @click="showResetPasswordConfirmation">
              <i class="fa-solid fa-key"></i>
              Reset Password
            </button>
            <button
                v-if="driverData && !driverData.lastLogin && driverData.onboardingStatus !== 'COMPLETE' && driverData.onboardingStatus !== 'PENDING_VERIFICATION'"
                class="btn btn-success" @click="showSendInviteConfirmation">
              <i class="fa-solid fa-envelope"></i>
              Send Registration Invite
            </button>
            <button class="btn btn-outline-primary" @click="toggleEditMode">
              <i class="icon-pencil-alt"></i>
              Edit Information
            </button>
          </div>
        </div>

        <div class="card-body">
          <div v-if="!editDriverInfo && !isNewDriver">
            <div class="mb-3">
              <span v-if="driverData && driverData.name" class="f-w-700 fs-5"> {{ driverData.name }}</span>
            </div>

            <div class="row align-items-center gy-4 mt-2">
              <div class="col-md-6">
                <ul>
                  <li class="d-flex align-items-center pb-2 fs-6">
                    <span class="f-w-700">Email:</span> &nbsp;
                    <span v-if="driverData && driverData.email"> {{ driverData.email }} </span>
                  </li>

                  <li class="d-flex align-items-center py-2 fs-6">
                    <span class="f-w-700">Mobile Number:</span> &nbsp;
                    <span v-if="driverData && driverData.phoneNumber"> {{ driverData.phoneNumber }} </span>
                  </li>

                  <li class="d-flex align-items-center pt-2 fs-6">
                    <span class="f-w-700">Started On:</span> &nbsp;
                    <span v-if="driverData && driverData.joinedOn">
                    {{ formatTime(driverData.joinedOn) }}
                  </span>
                  </li>
                  <li class="d-flex align-items-center pt-2 fs-6">
                    <span class="f-w-700">Last Login:</span>&nbsp;
                    <span v-if="driverData && driverData.lastLogin">
                        {{ formatTime(driverData.lastLogin) }}
                      </span>

                  </li>
                </ul>
              </div>

              <div class="col-md-6 border-start">
                <ul>
                  <li class="d-flex align-items-center gap-2 pb-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Email Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="driverData && driverData.emailVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 py-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Mobile Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="driverData && driverData.phoneNumberVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 py-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">License Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="driverData && driverData.licenseDocumentVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 py-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Insurance Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="driverData && driverData.insuranceDocumentVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="editDriverInfo || isNewDriver">
            <EntityForm model-constructor="Driver"
                        :api-url="`/api/driver`"
                        v-model:entity="driverFormData"
                        form-name="Driver"
                        :show-title="false"
                        :start-in-edit="true"
                        :entity-id="driverId"
                        :post-save="saveCallback"
                        :on-cancel-callback="cancelCallback"
                        :redirect-url=" (props.serviceProviderId) ? `/service-provider/${props.serviceProviderId}/driver` : `/admin/independent-driver`">

              <div v-if="driverFormData.state">
                <div class="row">
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-first-name-input">First Name</label>
                      <input id="driver-first-name-input" v-model="driverFormData.state.firstName"
                             class="form-control" type="text" placeholder="Johnny"/>
                      <div id="driver-first-name-error" class="invalid-feedback d-none">Please enter the first
                        name.
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-last-name-input">Last Name</label>
                      <input id="driver-last-name-input" v-model="driverFormData.state.lastName"
                             class="form-control" type="text" placeholder="Smith"/>
                      <div id="driver-last-name-error" class="invalid-feedback d-none">
                        Please enter the last name.
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-email-input">Email</label>
                      <input id="driver-email-input" v-model="driverFormData.state.email" class="form-control"
                             type="text" placeholder="email@example.com"/>
                      <div id="driver-email-error" class="invalid-feedback d-none">Please enter a valid email
                        address.
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-phone-number-input">Phone Number</label>
                      <input id="driver-phone-number-input" v-model="driverFormData.state.phoneNumber"
                             class="form-control"
                             type="text" placeholder="+1555123456"/>
                      <div id="driver-phone-number-error" class="invalid-feedback d-none">Please enter a valid phone
                        number.
                      </div>
                    </div>
                  </div>

                  <!-- Address Information -->
                  <div class="col-12">
                    <h5 class="mt-3 mb-3">Address Information</h5>
                  </div>

                  <div class="col-12">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-address-input">Address</label>
                      <input id="driver-address-input" v-model="driverFormData.state.address"
                             class="form-control" type="text" placeholder="Start typing to search for an address..."
                             ref="addressInput"/>
                      <div id="driver-address-error" class="invalid-feedback d-none">Please enter a valid address.</div>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-city-input">City</label>
                      <input id="driver-city-input" v-model="driverFormData.state.city"
                             class="form-control" type="text" placeholder="City"/>
                      <div id="driver-city-error" class="invalid-feedback d-none">Please enter a valid city.</div>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-state-input">State/Province</label>
                      <input id="driver-state-input" v-model="driverFormData.state.state"
                             class="form-control" type="text" placeholder="State"/>
                      <div id="driver-state-error" class="invalid-feedback d-none">Please enter a valid
                        state/province.
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-zip-code-input">Zip/Postal Code</label>
                      <input id="driver-zip-code-input" v-model="driverFormData.state.zipCode"
                             class="form-control" type="text" placeholder="12345"/>
                      <div id="driver-zip-code-error" class="invalid-feedback d-none">Please enter a valid zip/postal
                        code.
                      </div>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="driver-country-input">Country</label>
                      <select id="driver-country-input" v-model="driverFormData.state.country"
                              class="form-select">
                        <option value="">Select a country</option>
                        <option v-for="country in countries" :key="country" :value="country">
                          {{ country }}
                        </option>
                      </select>
                      <div id="driver-country-error" class="invalid-feedback d-none">Please select a country.</div>
                    </div>
                  </div>
                </div>
              </div>
            </EntityForm>

          </div>
        </div>
      </div>
    </div>

    <!-- Vehicle Information Section -->
    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center gap-2">
          <h3>Vehicle Information</h3>
        </div>

        <div class="card-body">
          <ul>
            <li class="d-flex align-items-center gap-2 border-bottom pb-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Service Type</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.vehicleType">
                  <span class="badge badge-light-primary"> {{ driverData.vehicleType }} </span>
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>

            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Make</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.vehicleMake">
                  {{ driverData.vehicleMake }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>

            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Model</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.vehicleModel">
                  {{ driverData.vehicleModel }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>
            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Color</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.vehicleColor">
                  {{ driverData.vehicleColor }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>
            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Number Plate</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.vehiclePlateNumber">
                  {{ driverData.vehiclePlateNumber }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>


            <!-- Rejection Reason -->
            <li v-if="driverData && driverData.vehicleRejectionReason"
                class="d-flex align-items-start gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700 text-danger">Rejection Reason</h5>
              </div>
              <div class="recent-text">
                <p class="text-danger mb-0">{{ driverData.vehicleRejectionReason }}</p>
              </div>
            </li>

            <!-- Verification Info -->
            <li v-if="driverData && driverData.vehicleVerified && driverData.vehicleVerifiedAt"
                class="d-flex align-items-start gap-2 pt-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Verified At</h5>
              </div>
              <div class="recent-text">
                <p class="mb-0">{{ formatTime(driverData.vehicleVerifiedAt) }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Vehicle Photos Section -->
    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center gap-2">
          <h3>Vehicle Photos</h3>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="driverData && driverData.vehiclePhotosVerified" class="badge badge-success">
              <i class="fa-solid fa-check-circle me-1"></i> Verified
            </span>
            <span v-else-if="driverData && driverData.vehiclePhotosRejectionReason" class="badge badge-danger">
              <i class="fa-solid fa-times-circle me-1"></i> Rejected
            </span>
            <span v-else class="badge badge-warning">
              <i class="fa-solid fa-clock me-1"></i> Pending
            </span>
          </div>
        </div>

        <div class="card-body">
          <ul>
            <li class="d-flex align-items-center gap-2 border-bottom pb-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Vehicle Photos</h5>
              </div>
              <div class="recent-text">
                <button v-if="driverData && driverData.vehiclePhotosUploaded"
                        class="btn btn-sm btn-outline-primary"
                        @click="downloadVehiclePhotos">
                  <i class="fa-solid fa-download me-1"></i> Download
                </button>
                <h5 v-else>Not Uploaded</h5>
              </div>
            </li>

            <!-- Rejection Reason -->
            <li v-if="driverData && driverData.vehiclePhotosRejectionReason"
                class="d-flex align-items-start gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700 text-danger">Rejection Reason</h5>
              </div>
              <div class="recent-text">
                <p class="text-danger mb-0">{{ driverData.vehiclePhotosRejectionReason }}</p>
              </div>
            </li>

            <!-- Verification Info -->
            <li v-if="driverData && driverData.vehiclePhotosVerified && driverData.vehiclePhotosVerifiedAt"
                class="d-flex align-items-start gap-2 pt-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Verified At</h5>
              </div>
              <div class="recent-text">
                <p class="mb-0">{{ formatTime(driverData.vehiclePhotosVerifiedAt) }}</p>
              </div>
            </li>
          </ul>

          <!-- Verification Actions -->
          <div v-if="driverData && driverData.vehiclePhotosUploaded && !driverData.vehiclePhotosVerified"
               class="d-flex gap-2 mt-3 pt-3">
            <button class="btn btn-success" @click="verifyVehicle">
              <i class="fa-solid fa-check me-1"></i> Approve Photos
            </button>
            <button class="btn btn-danger" @click="showRejectVehicleModal">
              <i class="fa-solid fa-times me-1"></i> Reject Photos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Driver License Section -->
    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center gap-2">
          <h3>Driver License Document</h3>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="driverData && driverData.licenseDocumentVerified" class="badge badge-success">
              <i class="fa-solid fa-check-circle me-1"></i> Verified
            </span>
            <span v-else-if="driverData && driverData.licenseRejectionReason" class="badge badge-danger">
              <i class="fa-solid fa-times-circle me-1"></i> Rejected
            </span>
            <span v-else class="badge badge-warning">
              <i class="fa-solid fa-clock me-1"></i> Pending
            </span>
          </div>
        </div>

        <div class="card-body">
          <ul>
            <li class="d-flex align-items-center gap-2 border-bottom pb-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">License Expiration Date</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.licenseExpirationDate">
                  {{ formatTime(driverData.licenseExpirationDate) }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>

            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">License Document</h5>
              </div>
              <div class="recent-text">
                <button v-if="driverData && driverData.licenseDocumentUrl"
                        class="btn btn-sm btn-outline-primary"
                        @click="downloadLicense">
                  <i class="fa-solid fa-download me-1"></i> Download
                </button>
                <h5 v-else>N/A</h5>
              </div>
            </li>

            <!-- Rejection Reason -->
            <li v-if="driverData && driverData.licenseRejectionReason"
                class="d-flex align-items-start gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700 text-danger">Rejection Reason</h5>
              </div>
              <div class="recent-text">
                <p class="text-danger mb-0">{{ driverData.licenseRejectionReason }}</p>
              </div>
            </li>

            <!-- Verification Info -->
            <li v-if="driverData && driverData.licenseVerified && driverData.licenseVerifiedAt"
                class="d-flex align-items-start gap-2 pt-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Verified At</h5>
              </div>
              <div class="recent-text">
                <p class="mb-0">{{ formatTime(driverData.licenseVerifiedAt) }}</p>
              </div>
            </li>
          </ul>

          <!-- Verification Actions -->
          <div v-if="driverData && driverData.licenseDocumentUrl && !driverData.licenseDocumentVerified"
               class="d-flex gap-2 mt-3 pt-3">
            <button class="btn btn-success" @click="verifyLicense">
              <i class="fa-solid fa-check me-1"></i> Approve License
            </button>
            <button class="btn btn-danger" @click="showRejectLicenseModal">
              <i class="fa-solid fa-times me-1"></i> Reject License
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Insurance Document Section -->
    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center gap-2">
          <h3>Insurance Document</h3>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="driverData && driverData.insuranceDocumentVerified" class="badge badge-success">
              <i class="fa-solid fa-check-circle me-1"></i> Verified
            </span>
            <span v-else-if="driverData && driverData.insuranceRejectionReason" class="badge badge-danger">
              <i class="fa-solid fa-times-circle me-1"></i> Rejected
            </span>
            <span v-else class="badge badge-warning">
              <i class="fa-solid fa-clock me-1"></i> Pending
            </span>
          </div>
        </div>

        <div class="card-body">
          <ul>
            <li class="d-flex align-items-center gap-2 border-bottom pb-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Insurance Expiration Date</h5>
              </div>
              <div class="recent-text">
                <h5 v-if="driverData && driverData.insuranceDocumentExpiryDate">
                  {{ formatTime(driverData.insuranceDocumentExpiryDate) }}
                </h5>
                <h5 v-else>N/A</h5>
              </div>
            </li>

            <li class="d-flex align-items-center gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Insurance Document</h5>
              </div>
              <div class="recent-text" v-if="driverData && driverData.insuranceDocumentUrl">
                <button class="btn btn-sm btn-outline-primary" @click="downloadInsurance">
                  <i class="fa-solid fa-download me-1"></i> Download
                </button>
              </div>
            </li>

            <!-- Rejection Reason -->
            <li v-if="driverData && driverData.insuranceRejectionReason"
                class="d-flex align-items-start gap-2 border-bottom py-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700 text-danger">Rejection Reason</h5>
              </div>
              <div class="recent-text">
                <p class="text-danger mb-0">{{ driverData.insuranceRejectionReason }}</p>
              </div>
            </li>

            <!-- Verification Info -->
            <li v-if="driverData && driverData.insuranceDocumentVerified && driverData.insuranceVerifiedAt"
                class="d-flex align-items-start gap-2 pt-2">
              <div class="flex-grow-1">
                <h5 class="f-w-700">Verified At</h5>
              </div>
              <div class="recent-text">
                <p class="mb-0">{{ formatTime(driverData.insuranceVerifiedAt) }}</p>
              </div>
            </li>
          </ul>

          <!-- Verification Actions -->
          <div v-if="driverData && !driverData.insuranceDocumentVerified && driverData.insuranceDocumentUrl"
               class="d-flex gap-2 mt-3 pt-3">
            <button class="btn btn-success" @click="verifyInsurance">
              <i class="fa-solid fa-check me-1"></i> Approve Insurance
            </button>
            <button class="btn btn-danger" @click="showRejectInsuranceModal">
              <i class="fa-solid fa-times me-1"></i> Reject Insurance
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12" v-if="!isNewDriver">
      <div class="card b-primary bg-light-primary">
        <div class="card-header bg-primary">
          <h5>
            <i class="fa-solid fa-list"></i>
            Ride Summary
          </h5>
        </div>
        <div class="card-body" v-if="driverData">
          <div class="col-12" style="justify-items: center;">
            <h4 class="f-w-400">Total Rides</h4>
            <h5 class="f-w-700">{{ driverData.totalRides }}</h5>
          </div>

          <div class="row mt-5">
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Today</h4>
              <h5 class="f-w-700">{{ driverData.totalRidesToday }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Week</h4>
              <h5 class="f-w-700">{{ driverData.totalRidesThisWeek }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Month</h4>
              <h5 class="f-w-700">{{ driverData.totalRidesThisMonth }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Last Month</h4>
              <h5 class="f-w-700">{{ driverData.totalRidesLastMonth }}</h5>
            </div>
          </div>
        </div>
        <div class="card-body" v-else> N/A</div>
      </div>
    </div>
    <div class="col-sm-6" v-if="!isNewDriver">
      <div class="card b-success bg-light-success">
        <div class="card-header bg-success">
          <h5>
            <i class="fa-solid fa-circle-check"></i>
            Completed Rides
          </h5>
        </div>
        <div class="card-body" v-if="driverData">
          <div class="col-12" style="justify-items: center;">
            <h4 class="f-w-400">Total Rides</h4>
            <h5 class="f-w-700">{{ driverData.completedRides }}</h5>
          </div>

          <div class="row mt-5">
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Today</h4>
              <h5 class="f-w-700">{{ driverData.completedRidesToday }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Week</h4>
              <h5 class="f-w-700">{{ driverData.completedRidesThisWeek }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Month</h4>
              <h5 class="f-w-700">{{ driverData.completedRidesThisMonth }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Last Month</h4>
              <h5 class="f-w-700">{{ driverData.completedRidesLastMonth }}</h5>
            </div>
          </div>
        </div>
        <div class="card-body" v-else> N/A</div>
      </div>
    </div>
    <div class="col-sm-6" v-if="!isNewDriver">
      <div class="card b-danger bg-light-danger">
        <div class="card-header bg-danger">
          <h5>
            <i class="fa-solid fa-circle-xmark "></i>
            Canceled Rides
          </h5>
        </div>
        <div class="card-body" v-if="driverData">
          <div class="col-12" style="justify-items: center;">
            <h4 class="f-w-400">Total Rides</h4>
            <h5 class="f-w-700">{{ driverData.cancelledRides }}</h5>
          </div>

          <div class="row mt-5">
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Today</h4>
              <h5 class="f-w-700">{{ driverData.cancelledRidesToday }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Week</h4>
              <h5 class="f-w-700">{{ driverData.cancelledRidesThisWeek }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">This Month</h4>
              <h5 class="f-w-700">{{ driverData.cancelledRidesThisMonth }}</h5>
            </div>
            <div class="col-sm-3" style="justify-items: center;">
              <h4 class="f-w-400">Last Month</h4>
              <h5 class="f-w-700">{{ driverData.cancelledRidesLastMonth }}</h5>
            </div>
          </div>
        </div>
        <div class="card-body" v-else> N/A</div>
      </div>
    </div>

    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header">
          <h3>Driver Rides</h3>
        </div>
        <div class="card-body">
          <DataTable
              :ajax="{ url: `/api/datatable/driver-rides?driverId=${props.driverId}`}"
              ref="ridesDataTable"
              class="table-responsive display"
              :columns="ridesColumns"/>
        </div>
      </div>
    </div>

    <div class="col-12" v-if="!isNewDriver">
      <div class="card">
        <div class="card-header">
          <h3>Driver Reviews</h3>
        </div>
        <div class="card-body">
          <DataTable
              :ajax="{ url: `/api/datatable/driver-reviews?driverId=${props.driverId}`}"
              ref="reviewsDataTable"
              class="table-responsive display"
              :columns="reviewsColumns"/>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup>
import {onMounted, ref, nextTick} from "vue";
import moment from "moment";
import {driverStatus, rideStatus, zonedDateTime} from "../utility/DatatableRenderer";
import {format} from "timeago.js";
import EntityForm from "./EntityForm.vue";
import DriverBackgroundCheckPanel from "./DriverBackgroundCheckPanel.vue";
import DriverTaxRegistrationPanel from "./DriverTaxRegistrationPanel.vue";
// Google Maps API is loaded via script tag
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import {showToast} from "../utility/toastNotificationUtility";
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import 'jquery-ui';
import {renderWithPoi} from '../utility/addressHelper';
import {newEntityId} from '../globals.js';

DataTable.use(DataTablesCore);

const props = defineProps({
  serviceProviderId: {type: String, required: true},
  driverId: {type: String, default: newEntityId},
  googleMapsApiKey: {type: String, required: false}
});

const isNewDriver = ref(props.driverId === newEntityId);

const initialCoordinates = {lat: 46.255803, lng: -63.136968};
const zoomLevel = 11;
let map = null;
let googleMaps = null; // Google Maps API reference

// List of countries for the dropdown
const countries = ["Canada", "United States"];

// Reference to the address input element
let addressInput = null;

// Initialize address autocomplete
const initAddressAutocomplete = () => {
  $(addressInput).autocomplete({
    source: (request, response) => {
      fetch(`/api/dispatch-ride/autocomplete?query=${encodeURIComponent(request.term)}`)
          .then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then((data) => {
            const suggestions = data.results.map((result) => ({
              label: renderWithPoi(result),
              value: result.address.freeformAddress,
              result,
            }));
            response(suggestions);
          })
          .catch((error) => {
            console.error('Error fetching suggestions:', error);
            response([]);
          });
    },
    minLength: 3,
    select: (event, ui) => {
      // Parse the address data and populate the form fields
      const addressData = ui.item.result;

      // Set the address field to the freeform address
      driverFormData.value.state.address = addressData.address.freeformAddress;

      // Extract and set city, state, zip code, and country
      if (addressData.address.municipality) {
        driverFormData.value.state.city = addressData.address.municipality;
      }

      if (addressData.address.countrySubdivision) {
        driverFormData.value.state.state = addressData.address.countrySubdivision;
      }

      if (addressData.address.extendedPostalCode) {
        driverFormData.value.state.zipCode = addressData.address.extendedPostalCode;
      }

      if (addressData.address.country) {
        driverFormData.value.state.country = addressData.address.country;
      }
    }
  }).autocomplete('instance')._renderItem = (ul, item) => {
    return $('<li>').append(item.label).appendTo(ul);
  };
};


const driverData = ref(undefined);
const editDriverInfo = ref(false);
const isProcessing = ref(false);
const ridesDataTable = ref(null);
const reviewsDataTable = ref(null);

const driverFormData = ref({});

// Define columns for rides datatable
const ridesColumns = [
  {
    data: "origin",
    title: "Pickup",
    render: (data, type, row) => {
      return row?.originLocationData?.address || 'N/A';
    }
  },
  {
    data: "destination",
    title: "Destination",
    render: (data, type, row) => {
      return row?.destinationLocationData?.address || 'N/A';
    }
  },
  {data: "rideStatus", title: "Status", render: rideStatus},
  {
    data: "fare",
    title: "Fare",
    render: (data, type, row) => {
      // totalWithTax is what the rider paid; totalFare is the pre-tax commission base. Formatted,
      // because the raw column serialises at whatever scale it was stored with.
      if (!row?.rideFare) return 'N/A';
      const charged = row.rideFare.totalWithTax ?? row.rideFare.totalFare;
      return '$' + Number(charged || 0).toFixed(2);
    }
  },
  {data: "orderDateTime", title: "Date", render: zonedDateTime},
  {
    title: '', orderable: false, searchable: false, render: (data, type, row, meta) => {
      return `<a class="btn btn-sm btn-outline-primary" target="blank" href="/service-provider/${props.serviceProviderId}/ride/${row.id}">
                Details <i class="fa-solid fa-arrow-up-right-from-square"></i></a>`;
    }
  }
];

// Define columns for reviews datatable
const reviewsColumns = [
  {
    data: "rider",
    title: "Rider",
    render: (data, type, row) => {
      return row?.reviewerNameAbbreviated ? row?.reviewerNameAbbreviated : 'N/A';
    }
  },
  {
    data: "createdOn",
    title: "Date",
    render: zonedDateTime
  },
  {
    data: "rating",
    title: "Rating",
    render: (data, type, row) => {
      let stars = '';
      for (let i = 1; i <= 5; i++) {
        stars += `<i class="fa-solid fa-star ${i <= data ? 'text-warning' : 'text-muted'}"></i>`;
      }
      return stars;
    }
  },
  {data: "review", title: "Review"}
];

const formatTime = (value) => {
  return `${moment(value).format('MMM DD, YYYY')} (${format(new Date(value))})`
};

/*
* Form edit functionality
* */
const toggleEditMode = () => {
  editDriverInfo.value = !editDriverInfo.value;
}

const cancelCallback = () => {
  toggleEditMode();
}

const saveCallback = () => {
  loadDriverData();
  toggleEditMode();
}


const initMap = () => {
  if (driverData.value && driverData.value.driverStatus !== 'OFFLINE') {
    // Load Google Maps API
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      const key = props.googleMapsApiKey;
      if (!key) {
        console.error('DriverDetail: googleMapsApiKey prop is missing — map will not load.');
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async&libraries=marker&callback=initGoogleMap`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      // Define the callback function globally
      window.initGoogleMap = () => {
        createMap();
      };
    } else {
      createMap();
    }
  }
};

const createMap = () => {
  googleMaps = google.maps;
  map = new googleMaps.Map(document.getElementById("map"), {
    center: initialCoordinates,
    zoom: zoomLevel,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    mapId: 'f5f337b511d9a28bc10fc480',
  });

  // Add driver marker if driver has current location
  if (driverData.value && driverData.value.currentLocationData) {
    const driverLocation = {
      lat: driverData.value.currentLocationData.latitude,
      lng: driverData.value.currentLocationData.longitude
    };

    // Build a custom car icon element for AdvancedMarkerElement
    const carIcon = document.createElement('img');
    carIcon.src = '/assets/images/car.png';
    carIcon.style.width = '30px';
    carIcon.style.height = '30px';

    new googleMaps.marker.AdvancedMarkerElement({
      position: driverLocation,
      map: map,
      content: carIcon,
      title: `Driver: ${driverData.value.name || 'Unknown'}`,
    });

    // Center map on driver location
    map.setCenter(driverLocation);
    map.setZoom(14);
  }
};


const loadDriverData = async () => {
  if (!isNewDriver.value) {
    const response = await fetch(`/api/driver-detail/${props.driverId}`);
    if (response.ok) {
      driverData.value = await response.json();
    }
  }
}


const showResetPasswordConfirmation = () => {
  if (!driverData.value) return;

  // Show the reset password confirmation using SweetAlert
  Swal.fire({
    title: 'Reset Password',
    text: `Are you sure you want to send a password reset email for ${driverData.value.name}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Reset Password',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33'
  }).then((result) => {
    if (result.isConfirmed) {
      resetPassword();
    }
  });
};

const resetPassword = async () => {
  if (!driverData.value) return;

  isProcessing.value = true;

  try {
    // Call the API endpoint to send a password reset email
    const response = await fetch(`/api/authenticate/send-password-reset/${driverData.value.id}`, {
      method: 'POST'
    });

    if (response.ok) {
      // Show success message
      showToast({
        title: 'success',
        body: `Password reset email for ${driverData.value.name} has been sent.`,
        type: 'success',
        delay: 7000
      });
    } else {
      // Get the error message from the response
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send password reset email');
    }
  } catch (error) {
    console.error('Error resetting password:', error);

    // Show error message
    showToast({
      title: 'error',
      body: error.message || 'There was an error sending the password reset email. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

const showSendInviteConfirmation = () => {
  if (!driverData.value) return;

  // Check if user has an email
  if (!driverData.value.email) {
    showToast({
      title: 'error',
      body: 'This driver does not have an email address. Please add an email address before sending an invite.',
      type: 'error',
      delay: 7000
    });
    return;
  }

  // Show the send invite confirmation using SweetAlert
  Swal.fire({
    title: 'Send Registration Invite',
    text: `Are you sure you want to send a registration invite to ${driverData.value.name} at ${driverData.value.email}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Send Invite',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d'
  }).then((result) => {
    if (result.isConfirmed) {
      sendInvite();
    }
  });
};

const sendInvite = async () => {
  if (!driverData.value || !driverData.value.email) return;

  isProcessing.value = true;

  try {
    // Call the API endpoint to send an invitation email
    const response = await fetch(`/api/authenticate/send-invitation/${driverData.value.id}`, {
      method: 'POST'
    });

    if (response.ok) {
      // Show success message
      showToast({
        title: 'success',
        body: `Registration invite has been sent to ${driverData.value.email}.`,
        type: 'success',
        delay: 7000
      });
    } else {
      // Get the error message from the response
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send registration invite');
    }
  } catch (error) {
    console.error('Error sending invite:', error);

    // Show error message
    showToast({
      title: 'error',
      body: error.message || 'There was an error sending the registration invite. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

const showResendStatusEmailConfirmation = () => {
  if (!driverData.value) return;

  Swal.fire({
    title: 'Resend Status Email',
    text: `Send the application status email to ${driverData.value.name} at ${driverData.value.email}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Send Email',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d'
  }).then((result) => {
    if (result.isConfirmed) {
      resendStatusEmail();
    }
  });
};

const resendStatusEmail = async () => {
  if (!driverData.value) return;

  isProcessing.value = true;

  try {
    const response = await fetch(`/api/admin/driver/${driverData.value.id}/resend-status-email`, {
      method: 'POST'
    });

    if (response.ok) {
      showToast({
        title: 'success',
        body: `Application status email sent to ${driverData.value.email}.`,
        type: 'success',
        delay: 7000
      });
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send status email');
    }
  } catch (error) {
    console.error('Error sending status email:', error);
    showToast({
      title: 'error',
      body: error.message || 'There was an error sending the status email. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

const downloadLicense = async () => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/download-license`);
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver-${props.driverId}-license.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      throw new Error('Failed to download license document');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to download license document',
      type: 'error'
    });
  }
};

const verifyLicense = async () => {
  const result = await Swal.fire({
    title: 'Approve License',
    text: 'Are you sure you want to approve this driver\'s license?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Approve',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-license`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({approved: true})
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'License has been approved',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to approve license');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to approve license',
      type: 'error'
    });
  }
};

const showRejectLicenseModal = async () => {
  const result = await Swal.fire({
    title: 'Reject License',
    text: 'Please provide a reason for rejection:',
    input: 'textarea',
    inputPlaceholder: 'Enter rejection reason...',
    showCancelButton: true,
    confirmButtonText: 'Reject',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc3545',
    inputValidator: (value) => {
      if (!value) {
        return 'You must provide a reason for rejection';
      }
    }
  });

  if (result.isConfirmed && result.value) {
    await rejectLicense(result.value);
  }
};

const rejectLicense = async (reason) => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-license`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        approved: false,
        rejectionReason: reason
      })
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'License has been rejected',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to reject license');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to reject license',
      type: 'error'
    });
  }
};

const verifyVehicle = async () => {
  const result = await Swal.fire({
    title: 'Approve Vehicle Photos',
    text: 'Are you sure you want to approve this driver\'s vehicle photos?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Approve',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-vehicle`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({approved: true})
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'Vehicle photos have been approved',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to approve vehicle photos');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to approve vehicle photos',
      type: 'error'
    });
  }
};

const showRejectVehicleModal = async () => {
  const result = await Swal.fire({
    title: 'Reject Vehicle Photos',
    text: 'Please provide a reason for rejection:',
    input: 'textarea',
    inputPlaceholder: 'Enter rejection reason...',
    showCancelButton: true,
    confirmButtonText: 'Reject',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc3545',
    inputValidator: (value) => {
      if (!value) {
        return 'You must provide a reason for rejection';
      }
    }
  });

  if (result.isConfirmed && result.value) {
    await rejectVehicle(result.value);
  }
};

const rejectVehicle = async (reason) => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-vehicle`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        approved: false,
        rejectionReason: reason
      })
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'Vehicle photos have been rejected',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to reject vehicle photos');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to reject vehicle photos',
      type: 'error'
    });
  }
};

const downloadVehiclePhotos = async () => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/download-vehicle-photos`);
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver-${props.driverId}-vehicle-photos.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      throw new Error('Failed to download vehicle photos');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to download vehicle photos',
      type: 'error'
    });
  }
};

const downloadInsurance = async () => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/download-insurance`);
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver-${props.driverId}-insurance.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      throw new Error('Failed to download insurance document');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to download insurance document',
      type: 'error'
    });
  }
};

const verifyInsurance = async () => {
  const result = await Swal.fire({
    title: 'Approve Insurance',
    text: 'Are you sure you want to approve this driver\'s insurance document?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Approve',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-insurance`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({approved: true})
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'Insurance document has been approved',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to approve insurance');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to approve insurance',
      type: 'error'
    });
  }
};

const showRejectInsuranceModal = async () => {
  const result = await Swal.fire({
    title: 'Reject Insurance',
    text: 'Please provide a reason for rejection:',
    input: 'textarea',
    inputPlaceholder: 'Enter rejection reason...',
    showCancelButton: true,
    confirmButtonText: 'Reject',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc3545',
    inputValidator: (value) => {
      if (!value) {
        return 'You must provide a reason for rejection';
      }
    }
  });

  if (result.isConfirmed && result.value) {
    await rejectInsurance(result.value);
  }
};

const rejectInsurance = async (reason) => {
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/verify-insurance`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        approved: false,
        reason: reason
      })
    });

    if (response.ok) {
      showToast({
        title: 'Success',
        body: 'Insurance document has been rejected',
        type: 'success'
      });
      await loadDriverData();
    } else {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to reject insurance');
    }
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Failed to reject insurance',
      type: 'error'
    });
  }
};

onMounted(() => {
  if (!isNewDriver.value) {
    loadDriverData().then(() => {
      initMap();
    });
  }

  // Initialize address autocomplete after the component is mounted
  nextTick(() => {
    if (isNewDriver.value || editDriverInfo.value) {
      addressInput = document.getElementById('driver-address-input');
      if (addressInput) {
        initAddressAutocomplete();
      }
    }
  });
})

</script>
